/**
 * Turkish Q (Windows KBDTUQ / Microsoft "Turkish Q Keyboard Layout"): maps a physical code to the
 * unshifted character that key emits. Letters are uppercase by table convention; the Turkish-specific
 * letters keep their unshifted lowercase form (dotless-i, dotted-i, g-breve, u-diaeresis, s-cedilla,
 * o-diaeresis, c-cedilla). The Q-row's I key emits dotless-i (U+0131); g-breve and u-diaeresis sit
 * right of P (BracketLeft/BracketRight); s-cedilla and dotted-i are on the home row right of L
 * (Semicolon/Quote); o-diaeresis and c-cedilla are bottom-row right of M (Comma/Period). The OEM keys
 * carry rotated punctuation: Minus emits "*", Equal "-", Backslash ",", Slash ".". Backquote emits a
 * quotation mark unshifted (e-acute on Shift); several OEM keys are dead/accent keys on a real board,
 * shown here as their unshifted glyph only. The ISO key left of Z (IntlBackslash, "less-than/greater-than") is outside the
 * US-Qwerty code block and is not bound by TheCore. Legends verified against the Microsoft KBDTUQ
 * base-state reference (learn.microsoft.com/globalization/keyboards/kbdtuq.html).
 */
export const TURKISH_Q_CODE_TO_CHAR: Record<string, string> = {
  Backquote: '"', // quotation mark (U+0022), VK_OEM_3 base (e-acute is the Shift glyph)
  Digit1: "1",
  Digit2: "2",
  Digit3: "3",
  Digit4: "4",
  Digit5: "5",
  Digit6: "6",
  Digit7: "7",
  Digit8: "8",
  Digit9: "9",
  Digit0: "0",
  Minus: "*", // asterisk (U+002A), VK_OEM_MINUS base
  Equal: "-", // hyphen-minus (U+002D), VK_OEM_PLUS base
  KeyQ: "Q",
  KeyW: "W",
  KeyE: "E",
  KeyR: "R",
  KeyT: "T",
  KeyY: "Y",
  KeyU: "U",
  KeyI: "ı", // dotless i (U+0131)
  KeyO: "O",
  KeyP: "P",
  BracketLeft: "ğ", // g with breve (U+011F)
  BracketRight: "ü", // u with diaeresis (U+00FC)
  Backslash: ",", // comma (U+002C), VK_OEM_5 base
  KeyA: "A",
  KeyS: "S",
  KeyD: "D",
  KeyF: "F",
  KeyG: "G",
  KeyH: "H",
  KeyJ: "J",
  KeyK: "K",
  KeyL: "L",
  Semicolon: "ş", // s with cedilla (U+015F)
  Quote: "i", // dotted i (U+0069); kept lowercase - its uppercase folds to dotless I, colliding with KeyI
  KeyZ: "Z",
  KeyX: "X",
  KeyC: "C",
  KeyV: "V",
  KeyB: "B",
  KeyN: "N",
  KeyM: "M",
  Comma: "ö", // o with diaeresis (U+00F6)
  Period: "ç", // c with cedilla (U+00E7)
  Slash: ".", // full stop (U+002E), VK_OEM_2 base
};

/**
 * The .SC2Hotkeys serialization token per code where Turkish Q's token diverges from charToken of its
 * character. SC2 binds these OEM/punctuation keys by virtual-key, so the file
 * names the key by its Turkish VK, not the glyph it emits. The Minus key carries no character in the
 * file (a pure virtual-key token, like the AZERTY exclamation-mark key) and serializes as "OEM8". The
 * punctuation keys rotate: Equal -> "Minus", Backslash -> "Comma", Comma -> "Slash", Period ->
 * "BackSlash", Slash -> "Period"; the bracket keys (g-breve, u-diaeresis) keep "BracketOpen"/
 * "BracketClose"; the home-row OEM keys (s-cedilla, dotted-i) keep "SemiColon"/"Apostrophe". Recovered
 * from TheCore's real US-Qwerty -> Turkey pair (both hands) and verified to reproduce those files
 * command-for-command. Most letters match charToken(map[code]) via the char path and a digit token is
 * positional, so both are omitted - except KeyI: its glyph is dotless-i (whose charToken is "ı"), but
 * SC2 serializes the key by virtual-key VK_I, so it carries an explicit "I" token. Backquote likewise
 * binds by VK_OEM_3, serializing as "Grave" (witnessed by the left hand; the right-hand file leaves its
 * one Backquote binding, ChatAllies, on "Grave" unconverted - a documented TheCore source quirk). The
 * token map is injective. The two i keys (dotless-i on KeyI, dotted-i on Quote) share a Unicode-folded
 * uppercase "I", so the char map is not a clean bijection - but both are virtual-key bound (this table),
 * so they resolve through the token path, never the char reverse.
 */
export const TURKISH_Q_CODE_TO_TOKEN: Record<string, string> = {
  Backquote: "Grave", // é dead key, VK_OEM_3 (witnessed by the left hand)
  KeyI: "I", // dotless-i key binds by virtual-key VK_I; the char path would emit "ı"
  Minus: "OEM8", // "*" key, char-less virtual-key token (VK_OEM_MINUS)
  Equal: "Minus", // "-" key, VK_OEM_PLUS
  BracketLeft: "BracketOpen", // ğ, VK_OEM_4
  BracketRight: "BracketClose", // ü, VK_OEM_6
  Backslash: "Comma", // "," key, VK_OEM_5
  Semicolon: "SemiColon", // ş, VK_OEM_1
  Quote: "Apostrophe", // dotted-i, VK_OEM_7
  Comma: "Slash", // ö, VK_OEM_COMMA
  Period: "BackSlash", // ç, VK_OEM_PERIOD
  Slash: "Period", // "." key, VK_OEM_2
};
